import React, { useState, useEffect, useRef, useCallback } from 'react'; // Force rebuild
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { Layout, ConfigProvider, message, Modal } from 'antd';
import AppSidebar from './components/AppSidebar';
import AppHeader from './components/AppHeader';
import { format } from 'date-fns';
import { useReactToPrint } from 'react-to-print';
import { exportToJSON, importFromJSON } from './utils/storage';
import { numberToWords } from './utils/numberToWords';
import { fetchClients, addClient as apiAddClient, deleteClient as apiDeleteClient, fetchInvoicesWithEditStatus, addInvoice as apiAddInvoice, fetchConfig, fetchInvoiceConfig, updateClient as apiUpdateClient, fetchFacilities, addFacility, updateFacility, deleteFacility, updateInvoiceNumber, fetchUsers, fetchInvoiceRules, fetchInvoiceTypes, checkInvoiceExists } from './utils/api';
import { initializeAuth, clearAuthData, isAuthenticated as checkIsAuthenticated, setGlobalAuthErrorCallback, isTokenExpiringSoon, refreshUserData } from './utils/auth';
import './App.css';
import { FileText, Cog, User, LogOut, ChevronDown, ChevronRight, Menu, ShieldCheck } from 'lucide-react';
// import protectionShieldAnimation from 'ProtectionShield.json';
import { Icon } from '@iconify/react';

// Import page components

import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import Invoice from './pages/Invoice';
import InvoiceHistory from './pages/InvoiceHistory';
import Configurations from './pages/Configurations';
import Login from './pages/Login';
import UsersPage from './pages/Users';
import Expenses from './pages/Expenses';
import ProfitLoss from './pages/ProfitLoss';
import Vendors from './pages/Vendors';
import Payables from './pages/Payables';
import PayableDetails from './pages/PayableDetails';
import VendorDetails from './pages/VendorDetails';
import ClientDetails from './pages/ClientDetails';
import CreditNotes from './pages/CreditNotes';
import CreateQuote from './pages/CreateQuote';
import QuoteHistory from './pages/QuoteHistory';
import TermSheets from './pages/TermSheets';
import TermSheetDetails from './pages/TermSheetDetails';
import Leads from './pages/Leads';
import InvoiceDetails from './pages/InvoiceDetails';
import QuoteDetails from './pages/QuoteDetails';
import Reports from './pages/Reports';
import Profile from './pages/Profile';


// Protected Route Component
const ProtectedRoute = ({ children, user, requiredRole = null, allowedRoles = null, requiredModule = null }) => {
  if (!user) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '50vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  // Check if user has access based on requiredRole or allowedRoles
  let hasRoleAccess = true; // Default to true if no role requirement
  if (allowedRoles) {
    hasRoleAccess = allowedRoles.includes(user.role);
  } else if (requiredRole) {
    hasRoleAccess = user.role === requiredRole;
  }

  // Check module access if required
  let hasModuleAccess = true;
  if (user?.role === 'admin') {
    hasModuleAccess = true;
  } else if (requiredModule) {
    // If user doesn't have moduleAccess field, they might be using old session data
    if (!user.moduleAccess) {
      hasModuleAccess = false;
    } else {
      const moduleValue = user.moduleAccess[requiredModule];
      hasModuleAccess = moduleValue === true;
    }
  }

  if (!hasRoleAccess || !hasModuleAccess) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '50vh',
        fontSize: '18px',
        color: '#666'
      }}>
        <div style={{ marginBottom: '16px' }}>
          <Cog size={48} color="#666" />
        </div>
        <h2 style={{ margin: '0 0 8px 0', color: '#333' }}>Access Denied</h2>
        <p style={{ margin: 0, textAlign: 'center' }}>
          You don't have permission to access this page.<br />
          Please contact your administrator for access.
        </p>
      </div>
    );
  }



  return children;
};

const INACTIVITY_GRACE_PERIOD_SECONDS = 5 * 60;
const COUNTDOWN_DURATION_SECONDS = 30;

// Add this CSS to the top or bottom of the file (or in App.css):
/* Custom Tooltip CSS */
const tooltipStyles = `
.custom-tooltip {
  position: relative;
  display: inline-block;
}
.custom-tooltip-text {
  visibility: hidden;
  width: max-content;
  max-width: 200px;
  background-color: #555;
  color: #fff;
  text-align: center;
  border-radius: 6px;
  padding: 8px 16px;
  position: absolute;
  z-index: 100;
  bottom: 125%;
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  transition: opacity 0.2s;
  font-size: 15px;
  white-space: nowrap;
}
.custom-tooltip-text::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -7px;
  border-width: 7px;
  border-style: solid;
  border-color: #555 transparent transparent transparent;
}
.custom-tooltip:hover .custom-tooltip-text {
  visibility: visible;
  opacity: 1;
}
`;
if (typeof document !== 'undefined' && !document.getElementById('custom-tooltip-style')) {
  const style = document.createElement('style');
  style.id = 'custom-tooltip-style';
  style.innerHTML = tooltipStyles;
  document.head.appendChild(style);
}

// Sidebar component


// Main App component
function App() {
  // Get current financial year
  const getCurrentFinancialYear = () => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    if (currentMonth >= 4) {
      return `${currentYear}-${currentYear + 1}`;
    } else {
      return `${currentYear - 1}-${currentYear}`;
    }
  };

  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);

  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [users, setUsers] = useState([]);
  const [config, setConfig] = useState({});
  const [invoiceConfig, setInvoiceConfig] = useState({});
  const [facilities, setFacilities] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedInvoiceCategoryId, setSelectedInvoiceCategoryId] = useState('');
  const [invoiceItems, setInvoiceItems] = useState([{ description: '', rate: '', amount: '', hsn: '', qty: '', unitPrice: '', uom: '' }]);
  const [invoiceNumber, setInvoiceNumber] = useState('1');
  const [invoiceDate, setInvoiceDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [financialYear, setFinancialYear] = useState(getCurrentFinancialYear());
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [poDate, setPoDate] = useState('');
  const [purchaseInvoiceNumber, setPurchaseInvoiceNumber] = useState('');
  const [purchaseInvoiceFile, setPurchaseInvoiceFile] = useState(null);
  const [isAdhocClientInvoice, setIsAdhocClientInvoice] = useState(false);
  const [vendorName, setVendorName] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [vendorInvoiceDate, setVendorInvoiceDate] = useState('');
  const [vendorPoNumber, setVendorPoNumber] = useState('');
  const [vendorPoDate, setVendorPoDate] = useState('');
  const [department, setDepartment] = useState('');
  const [showShipTo, setShowShipTo] = useState('no');
  const [isLoading, setIsLoading] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(true);
  const [roundOffEnabled, setRoundOffEnabled] = useState(true);
  const [notes, setNotes] = useState('');

  const [invoiceRules, setInvoiceRules] = useState([]);
  const [invoiceTypes, setInvoiceTypes] = useState([]);
  const [isCountdownVisible, setIsCountdownVisible] = useState(false);
  const [sessionTimeLeft, setSessionTimeLeft] = useState(COUNTDOWN_DURATION_SECONDS);
  const [graceTimeLeft, setGraceTimeLeft] = useState(INACTIVITY_GRACE_PERIOD_SECONDS);
  const idleTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const graceIntervalRef = useRef(null);

  // Check authentication on mount
  useEffect(() => {
    // Set up global auth error callback
    setGlobalAuthErrorCallback(() => {
      setIsAuthenticated(false);
      setUser(null);
    });

    const userData = initializeAuth();
    if (userData) {
      // Always try to refresh user data to ensure we have the latest module access
      refreshUserData().then(freshUserData => {
        if (freshUserData) {
          setUser(freshUserData);
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
          setUser(null);
        }
      }).catch((error) => {
        // If refresh fails, use the stored data
        setUser(userData);
        setIsAuthenticated(true);
      });
    } else {
      setIsAuthenticated(false);
      setUser(null);
    }
  }, []);

  // Handle login
  const handleLogin = (userData) => {
    setIsAuthenticated(true);
    setUser(userData);
  };

  // Handle logout
  const clearSessionTimers = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    if (graceIntervalRef.current) {
      clearInterval(graceIntervalRef.current);
      graceIntervalRef.current = null;
    }
  }, []);

  const handleLogout = useCallback(() => {
    clearSessionTimers();
    clearAuthData();
    setIsAuthenticated(false);
    setUser(null);
    setGraceTimeLeft(INACTIVITY_GRACE_PERIOD_SECONDS);
    setSessionTimeLeft(COUNTDOWN_DURATION_SECONDS);
    setIsCountdownVisible(false);
  }, [clearSessionTimers]);

  const resetSessionTimeout = useCallback(() => {
    clearSessionTimers();
    setIsCountdownVisible(false);
    setSessionTimeLeft(COUNTDOWN_DURATION_SECONDS);
    setGraceTimeLeft(INACTIVITY_GRACE_PERIOD_SECONDS);

    graceIntervalRef.current = setInterval(() => {
      setGraceTimeLeft((prev) => {
        const next = Math.max(0, prev - 1);
        if (next <= 0 && graceIntervalRef.current) {
          clearInterval(graceIntervalRef.current);
          graceIntervalRef.current = null;
        }
        return next;
      });
    }, 1000);

    idleTimerRef.current = setTimeout(() => {
      if (graceIntervalRef.current) {
        clearInterval(graceIntervalRef.current);
        graceIntervalRef.current = null;
      }
      setGraceTimeLeft(0);
      setIsCountdownVisible(true);
      setSessionTimeLeft(COUNTDOWN_DURATION_SECONDS);

      countdownIntervalRef.current = setInterval(() => {
        setSessionTimeLeft((prev) => {
          const next = Math.max(0, prev - 1);
          if (next <= 0 && countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          return next;
        });
      }, 1000);

      logoutTimerRef.current = setTimeout(() => {
        setSessionTimeLeft(0);
        clearSessionTimers();
        handleLogout();
      }, COUNTDOWN_DURATION_SECONDS * 1000);
    }, INACTIVITY_GRACE_PERIOD_SECONDS * 1000);
  }, [clearSessionTimers, handleLogout]);



  // Fetch data when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      // CRITICAL: Clear invoices first to prevent stale data
      console.log('[DEBUG] Clearing invoices before fetch to prevent stale data');
      setInvoices([]);

      Promise.all([
        fetchClients().catch(err => []),
        fetchInvoicesWithEditStatus().catch(err => {
          console.error('[DEBUG] Failed to fetch invoices:', err);
          console.error('[DEBUG] Error details:', {
            message: err.message,
            stack: err.stack,
            name: err.name
          });
          alert('Failed to fetch invoices. Please refresh the page. Error: ' + (err.message || 'Unknown error'));
          return [];
        }).then(data => {
          console.log('[DEBUG] fetchInvoicesWithEditStatus returned:', {
            isArray: Array.isArray(data),
            length: Array.isArray(data) ? data.length : 'not an array',
            data: data
          });
          return data;
        }),
        fetchUsers().catch(err => []),
        fetchConfig().catch(err => ({})),
        fetchInvoiceConfig().catch(err => ({})),
        fetchFacilities().catch(err => []),
        fetchInvoiceRules().catch(err => []),
        fetchInvoiceTypes().catch(err => [])
      ]).then(([clientsData, invoicesData, usersData, configData, invoiceConfigData, facilitiesData, rulesData, typesData]) => {
        if (clientsData) setClients(clientsData);

        // Handle invoices - ensure we're setting exactly what the API returns
        console.log('[DEBUG] Processing invoices response:', {
          isArray: Array.isArray(invoicesData),
          length: Array.isArray(invoicesData) ? invoicesData.length : 'not an array',
          type: typeof invoicesData,
          value: invoicesData
        });

        if (invoicesData && Array.isArray(invoicesData)) {
          console.log(`[DEBUG] Fetched ${invoicesData.length} invoices from API`);

          // Log all invoice IDs immediately
          const invoiceIds = invoicesData.map(inv => inv._id || inv.id);
          console.log(`[DEBUG] Invoice IDs received from API:`, invoiceIds);

          invoicesData.forEach((inv, index) => {
            console.log(`[DEBUG] Invoice ${index + 1}:`, {
              id: inv._id || inv.id,
              invoiceNumber: inv.invoiceNumber,
              invoiceNumberType: typeof inv.invoiceNumber,
              invoiceNumberValue: JSON.stringify(inv.invoiceNumber),
              invoiceDate: inv.invoiceDate,
              clientName: inv.client?.name || 'No client'
            });

            // Verify invoiceNumber exists
            if (!inv.invoiceNumber) {
              console.error(`[DEBUG] ERROR: Invoice ${inv._id || inv.id} missing invoiceNumber in API response!`);
            }
          });

          // Set invoices - this should replace any stale data
          console.log(`[DEBUG] Setting ${invoicesData.length} invoices to state`);
          setInvoices(invoicesData);

          // Verify state was set correctly (in next render cycle)
          setTimeout(() => {
            console.log('[DEBUG] State verification will happen on next render');
          }, 0);
        } else {
          console.warn('[DEBUG] No invoices data returned from API or not an array:', invoicesData);
          // Explicitly set empty array to clear any stale data
          setInvoices([]);
        }
        if (usersData) setUsers(usersData);
        if (configData) {
          setConfig(configData);
          if (configData.nextInvoiceNumber !== undefined && configData.nextInvoiceNumber !== null) {
            setInvoiceNumber(configData.nextInvoiceNumber.toString());
          }
        }
        if (invoiceConfigData) setInvoiceConfig(invoiceConfigData);
        if (facilitiesData) setFacilities(facilitiesData);
        if (rulesData) setInvoiceRules(Array.isArray(rulesData) ? rulesData : []);
        if (typesData) setInvoiceTypes(Array.isArray(typesData) ? typesData : []);
        setIsLoading(false);
      });
    } else {
      // When not authenticated, clear all invoices to prevent stale data
      console.log('[DEBUG] User not authenticated - clearing invoices');
      setInvoices([]);
      setIsLoading(false);
    }
  }, [isAuthenticated, user, handleLogout]);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      clearSessionTimers();
      setIsCountdownVisible(false);
      setSessionTimeLeft(COUNTDOWN_DURATION_SECONDS);
      setGraceTimeLeft(INACTIVITY_GRACE_PERIOD_SECONDS);
      return;
    }

    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    const handleActivity = () => {
      resetSessionTimeout();
    };

    activityEvents.forEach(eventName =>
      window.addEventListener(eventName, handleActivity, { passive: true })
    );

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        resetSessionTimeout();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    resetSessionTimeout();

    return () => {
      activityEvents.forEach(eventName =>
        window.removeEventListener(eventName, handleActivity)
      );
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearSessionTimers();
    };
  }, [isAuthenticated, user, resetSessionTimeout, clearSessionTimers]);

  // Check token expiry periodically
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    // Check token validity immediately
    if (!checkIsAuthenticated()) {
      handleLogout();
      return;
    }

    // Set up periodic token checking (every minute)
    const tokenCheckInterval = setInterval(() => {
      if (!checkIsAuthenticated()) {
        clearInterval(tokenCheckInterval);
        handleLogout();
      } else if (isTokenExpiringSoon()) {
        // Token expiring soon, consider implementing refresh logic
      }
    }, 60000); // Check every minute

    return () => clearInterval(tokenCheckInterval);
  }, [isAuthenticated, user, handleLogout]);

  const handleStayLoggedIn = useCallback(() => {
    resetSessionTimeout();
  }, [resetSessionTimeout]);

  const formatTime = useCallback((seconds) => {
    const clamped = Math.max(0, seconds);
    const minutes = Math.floor(clamped / 60).toString().padStart(2, '0');
    const secs = (clamped % 60).toString().padStart(2, '0');
    return `${minutes}:${secs}`;
  }, []);

  // Calculate invoice totals for rent invoice
  const calculateTotals = () => {
    const cgstRate = config && config.cgstRate !== undefined ? config.cgstRate : 0;
    const sgstRate = config && config.sgstRate !== undefined ? config.sgstRate : 0;
    const subtotal = Math.round(invoiceItems.reduce((sum, item) => sum + Number(item.amount || 0), 0) * 100) / 100;
    const cgst = Math.round(subtotal * (cgstRate / 100) * 100) / 100;
    const sgst = Math.round(subtotal * (sgstRate / 100) * 100) / 100;
    const grossTotal = subtotal + cgst + sgst;
    const roundedTotal = Math.round(grossTotal);
    const roundOff = roundOffEnabled ? Math.round((roundedTotal - grossTotal) * 100) / 100 : 0;
    const total = roundOffEnabled ? roundedTotal : Math.round(grossTotal * 100) / 100;
    return { subtotal, cgst, sgst, total, roundOff };
  };

  // Helpers to coerce numbers reliably from any input (for adhoc invoices)
  const toNumber = (value, fallback = 0) => {
    const cleaned = String(value ?? '').replace(/[^0-9.-]/g, '');
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : fallback;
  };

  // Calculate item totals for adhoc invoice
  const calculateAdhocItemTotals = (item) => {
    const qty = Math.max(0, toNumber(item.qty, 1));
    const unitPriceRaw = toNumber(item.unitPrice);
    const unitPrice = unitPriceRaw > 0 ? unitPriceRaw : (qty > 0 ? toNumber(item.netAmount) / qty : 0);
    const taxRate = Math.max(0, toNumber(item.tax ?? item.taxPercent, 0));

    const netAmount = unitPrice * qty;
    const taxAmount = netAmount * (taxRate / 100);
    const totalAmount = netAmount + taxAmount;

    return { netAmount, taxAmount, totalAmount };
  };

  // Calculate adhoc invoice totals
  const calculateAdhocTotals = () => {
    // Recompute from current inputs instead of trusting stored fields
    const totals = invoiceItems.reduce((acc, item) => {
      const { netAmount, taxAmount } = calculateAdhocItemTotals(item);
      acc.net += netAmount;
      acc.tax += taxAmount;
      return acc;
    }, { net: 0, tax: 0 });
    const totalNetAmount = Math.round(totals.net * 100) / 100;
    const totalTaxAmount = Math.round(totals.tax * 100) / 100;
    const grossAmount = totalNetAmount + totalTaxAmount;
    const roundedTotal = Math.round(grossAmount);
    const roundOff = roundOffEnabled ? Math.round((roundedTotal - grossAmount) * 100) / 100 : 0;
    const totalAmount = roundOffEnabled ? roundedTotal : Math.round(grossAmount * 100) / 100;
    return { totalNetAmount, totalTaxAmount, totalAmount, roundOff };
  };

  const { subtotal, cgst, sgst } = calculateTotals();

  // Handle invoice item changes for rent invoice
  const handleItemChange = (index, field, value) => {
    const newItems = [...invoiceItems];
    newItems[index][field] = value;

    if (field === 'rate') {
      const rate = parseFloat(newItems[index].rate) || 0;
      newItems[index].amount = rate;
    }

    setInvoiceItems(newItems);
  };

  // Add new invoice item for rent invoice
  const addInvoiceItem = () => {
    setInvoiceItems([...invoiceItems, { description: '', rate: '', amount: '', hsn: '', qty: '', unitPrice: '', uom: '' }]);
  };

  // Remove invoice item
  const removeInvoiceItem = (index) => {
    if (invoiceItems.length > 1) {
      const newItems = invoiceItems.filter((_, i) => i !== index);
      setInvoiceItems(newItems);
    }
  };

  // Add new client
  // Refresh only the invoice number from server
  const refreshInvoiceNumber = async () => {
    try {
      const configData = await fetchConfig();
      if (configData && configData.nextInvoiceNumber !== undefined) {
        setInvoiceNumber(configData.nextInvoiceNumber.toString());
        setConfig(prev => ({ ...prev, nextInvoiceNumber: configData.nextInvoiceNumber }));
        message.success('Invoice number refreshed to latest available.');
      }
    } catch (err) {
      console.error('Failed to refresh invoice number:', err);
      message.error('Failed to refresh invoice number from server.');
    }
  };

  const addClient = async (clientData) => {
    try {
      const newClient = await apiAddClient(clientData);
      setClients((prev) => [...prev, newClient]);
    } catch (err) {
      alert('Failed to add client');
    }
  };

  // Remove client
  const removeClient = async (clientId) => {
    try {
      await apiDeleteClient(clientId);
      setClients((prev) => prev.filter(client => client._id !== clientId));
    } catch (err) {
      alert('Failed to delete client');
    }
  };

  // Update client
  const updateClient = async (clientId, clientData) => {
    try {
      const updatedClient = await apiUpdateClient(clientId, clientData);
      setClients((prev) => prev.map(client =>
        client._id === clientId ? updatedClient : client
      ));
    } catch (err) {
      alert('Failed to update client');
    }
  };


  // Generate invoice
  const generateInvoice = async (isAdhocFormat = null, bankAccount = null, invoiceCategoryId = null, paymentTerm = null) => {
    // Check if user is properly authenticated
    if (!user) {
      alert('Please log in again. User authentication is required to create invoices.');
      return;
    }

    // Some older user objects might have `_id`. Normalize here just in case UI state
    const userId = user.id || user._id;
    if (!userId) {
      alert('User authentication error. Please log out and log back in.');
      return;
    }

    // Validate ObjectId format (24 character hex string)
    const objectIdRegex = /^[0-9a-fA-F]{24}$/;
    if (!objectIdRegex.test(userId)) {
      alert('Invalid user ID format. Please log out and log back in.');
      return;
    }

    if (!selectedClient || invoiceItems.some(item => !item.description)) {
      alert('Please select a client and fill all item descriptions');
      return;
    }

    // CRITICAL: Validate invoiceNumber is present and valid
    if (!invoiceNumber || invoiceNumber === null || invoiceNumber === undefined || String(invoiceNumber).trim() === '') {
      Modal.error({
        title: 'Invoice Number Required',
        content: 'Invoice number is required. Please set a valid invoice number before creating the invoice.'
      });
      console.error('[ERROR] Cannot create invoice - invoiceNumber is missing:', invoiceNumber);
      return;
    }

    // Check for duplicate invoice number before proceeding
    try {
      const { exists } = await checkInvoiceExists(String(invoiceNumber).trim(), financialYear);
      if (exists) {
        Modal.error({
          title: 'Duplicate Invoice Number',
          content: `Invoice number ${invoiceNumber} already exists for financial year ${financialYear}. Please use a different number.`,
          okText: 'Update to Latest Number',
          onOk: () => {
            refreshInvoiceNumber();
          }
        });
        return;
      }
    } catch (err) {
      console.warn('Failed to check for duplicate invoice number:', err);
      // Continue anyway, backend will catch it
    }

    const clientPool = clients;
    const clientObj = clientPool.find(c => c._id === selectedClient || c.id === selectedClient);
    if (!clientObj) {
      alert('Selected client not found');
      return;
    }

    // Determine invoice format
    // If isAdhocFormat is explicitly passed, use it; otherwise check path as fallback
    let isAdhocInvoice = false;
    if (isAdhocClientInvoice) {
      isAdhocInvoice = true;
    } else if (isAdhocFormat !== null) {
      isAdhocInvoice = isAdhocFormat;
    } else {
      // Fallback to path check (for backward compatibility)
      isAdhocInvoice = window.location.pathname === '/adhoc-invoice';
    }

    let invoiceData;
    if (isAdhocInvoice) {
      const { totalNetAmount, totalTaxAmount, totalAmount, roundOff } = calculateAdhocTotals();

      // Prepare invoice items for backend (recalculate values to ensure accuracy)
      const processedItems = invoiceItems.map(item => {
        const { netAmount, taxAmount, totalAmount: itemTotalAmount } = calculateAdhocItemTotals(item);
        return {
          description: item.description,
          hsn: item.hsn,
          qty: Number(item.qty || 1),
          unitPrice: Number(item.unitPrice || 0),
          uom: item.uom || '',
          netAmount: Math.round(netAmount * 100) / 100,
          tax: Number(item.tax || item.taxPercent || 0),
          taxAmount: Math.round(taxAmount * 100) / 100,
          totalAmount: Math.round(itemTotalAmount * 100) / 100,
          IsServc: item.IsServc || 'N'
        };
      });

      invoiceData = {
        invoiceNumber: String(invoiceNumber).trim(),
        invoiceDate,
        financialYear,
        placeOfSupply: invoiceConfig.defaultPlaceOfSupply || 'Karnataka',
        poNumber,
        poDate,
        purchaseInvoiceNumber: purchaseInvoiceNumber ? String(purchaseInvoiceNumber).trim() : '',
        purchaseInvoiceUrl: purchaseInvoiceFile?.url || '',
        purchaseInvoiceOriginalName: purchaseInvoiceFile?.originalName || '',
        isAdhocClientInvoice: Boolean(isAdhocClientInvoice),
        vendorName: vendorName || '',
        vendorId: vendorId || null,
        vendorInvoiceDate: vendorInvoiceDate || '',
        vendorPoNumber: vendorPoNumber ? String(vendorPoNumber).trim() : '',
        vendorPoDate: vendorPoDate || '',
        department: department || '',
        showShipTo,
        client: clientObj._id,
        items: processedItems,
        totals: {
          subtotal: totalNetAmount,
          cgst: totalTaxAmount / 2,
          sgst: totalTaxAmount / 2,
          roundOff,
          total: totalAmount
        },
        amountInWords: numberToWords(totalAmount),
        invoiceType: 'adhoc',
        creator: userId,
        bankAccount: bankAccount || null,
        invoiceCategoryId: invoiceCategoryId || null,
        // Payment term & due date
        dueDate: paymentTerm?.dueDate || null,
        paymentTerm: paymentTerm?.label || '',
        paymentTermDays: typeof paymentTerm?.days === 'number' ? paymentTerm.days : undefined,
        notes: notes || ''
      };
    } else {
      // For rent invoices, ensure all items have IsServc: "Y" since rent is always a service
      const rentInvoiceItems = invoiceItems.map(item => ({
        ...item,
        IsServc: 'Y'
      }));

      invoiceData = {
        invoiceNumber: String(invoiceNumber).trim(),
        invoiceDate,
        financialYear,
        placeOfSupply: invoiceConfig.defaultPlaceOfSupply || 'Karnataka',
        poNumber,
        poDate,
        purchaseInvoiceNumber: purchaseInvoiceNumber ? String(purchaseInvoiceNumber).trim() : '',
        purchaseInvoiceUrl: purchaseInvoiceFile?.url || '',
        purchaseInvoiceOriginalName: purchaseInvoiceFile?.originalName || '',
        isAdhocClientInvoice: Boolean(isAdhocClientInvoice),
        vendorName: vendorName || '',
        vendorId: vendorId || null,
        vendorInvoiceDate: vendorInvoiceDate || '',
        vendorPoNumber: vendorPoNumber ? String(vendorPoNumber).trim() : '',
        vendorPoDate: vendorPoDate || '',
        department: department || '',
        showShipTo,
        client: clientObj._id,
        items: rentInvoiceItems,
        totals: { subtotal, cgst, sgst, roundOff: calculateTotals().roundOff, total: calculateTotals().total },
        amountInWords: numberToWords(calculateTotals().total),
        invoiceType: 'rent',
        creator: userId,
        bankAccount: bankAccount || null,
        invoiceCategoryId: invoiceCategoryId || null,
        // Payment term & due date
        dueDate: paymentTerm?.dueDate || null,
        paymentTerm: paymentTerm?.label || '',
        paymentTermDays: typeof paymentTerm?.days === 'number' ? paymentTerm.days : undefined,
        notes: notes || ''
      };
    }

    try {
      const savedInvoice = await apiAddInvoice(invoiceData);

      // Refetch invoices to ensure the new invoice is properly formatted with editStatus
      // and matches the structure expected by InvoiceHistory
      try {
        const updatedInvoices = await fetchInvoicesWithEditStatus();
        setInvoices(updatedInvoices);
      } catch (refreshError) {
        console.error('Failed to refresh invoices list:', refreshError);
        // Fallback: Add the invoice manually if refresh fails
        const createdDate = savedInvoice.createdAt ? new Date(savedInvoice.createdAt) : new Date();
        const nextMonth = new Date(createdDate.getFullYear(), createdDate.getMonth() + 1, 11);
        const now = new Date();
        const isEditable = !savedInvoice.isEditable ? false : now <= (savedInvoice.editDeadline ? new Date(savedInvoice.editDeadline) : nextMonth);
        const deadline = savedInvoice.editDeadline || nextMonth;
        const daysUntilDeadline = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));

        const invoiceWithEditStatus = {
          ...savedInvoice,
          editStatus: {
            isEditable,
            deadline: (deadline instanceof Date ? deadline : new Date(deadline)).toISOString(),
            daysUntilDeadline: isEditable ? Math.max(0, daysUntilDeadline) : 0,
            isPastDeadline: !isEditable
          }
        };
        setInvoices((prev) => [invoiceWithEditStatus, ...prev]);
      }

      // Increment the next invoice number in configuration
      const currentNumber = parseInt(invoiceNumber) || 1;
      const newNextNumber = currentNumber + 1;

      // Update only the invoice number using the new API (accessible to both admin and user roles)
      await updateInvoiceNumber(newNextNumber);

      // Update local config state
      setConfig(prev => ({ ...prev, nextInvoiceNumber: newNextNumber }));
      setInvoiceConfig(prev => ({ ...prev, nextInvoiceNumber: newNextNumber }));

      // Reset form based on invoice type
      if (isAdhocInvoice) {
        setInvoiceItems([{ description: '', unitPrice: 0, qty: 1, netAmount: 0, tax: 0, taxAmount: 0, totalAmount: 0, hsn: '', uom: '' }]);
      } else {
        setInvoiceItems([{ description: '', rate: 0, amount: 0, hsn: '', uom: '' }]);
      }
      setSelectedClient('');
      setSelectedInvoiceCategoryId('');
      setInvoiceNumber(newNextNumber.toString());
      setShowInvoicePreview(false);
      setShowShipTo('no');
      setPurchaseInvoiceNumber('');
      setPurchaseInvoiceFile(null);
      setIsAdhocClientInvoice(false);
      setVendorName('');
      setVendorId('');
      setVendorInvoiceDate('');
      setVendorPoNumber('');
      setVendorPoDate('');
      setDepartment('');
      message.success('Invoice generated successfully!');
      
      // Refresh the app after creation
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err) {
      const errorMessage = err.message || 'Failed to create invoice';
      message.error(`Failed to create invoice: ${errorMessage}`);
    }
  };

  // Print invoice
  const printRef = React.useRef();
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
  });

  // Export data
  const handleExport = () => {
    const data = { clients, invoices };
    exportToJSON(data, 'invoice-data.json');
  };

  // Import data
  const handleImport = (event) => {
    const file = event.target.files[0];
    if (file) {
      importFromJSON(file)
        .then((data) => {
          if (data.clients) setClients(data.clients);
          if (data.invoices) setInvoices(data.invoices);
          alert('Data imported successfully!');
        })
        .catch((error) => {
          alert('Error importing data: ' + error.message);
        });
    }
  };

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return (
      <Router>
        <Login onLogin={handleLogin} />
      </Router>
    );
  }

  return (
    <Router>
      <ConfigProvider theme={{ token: { fontFamily: 'Fira Sans' } }}>
        <Layout style={{ minHeight: '100vh', flexDirection: 'column' }}>
          <AppHeader
            user={user}
            onLogout={handleLogout}
            sessionGraceLabel={formatTime(graceTimeLeft)}
            mobileOpen={isMobileSidebarOpen}
            setMobileOpen={setIsMobileSidebarOpen}
            config={config}
          />
          <Layout>
            <AppSidebar
              user={user}
              onLogout={handleLogout}
              mobileOpen={isMobileSidebarOpen}
              setMobileOpen={setIsMobileSidebarOpen}
              onNavigate={() => setIsMobileSidebarOpen(false)}
              sessionGraceLabel={formatTime(graceTimeLeft)}
            />
            <Layout.Content style={{ background: '#f8f9fa', minHeight: 'calc(100vh - 64px)', padding: 0 }}>
              {isLoading ? (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100vh',
                  fontSize: '18px',
                  color: '#666'
                }}>
                  Loading...
                </div>
              ) : (
                <Routes>
                  <Route path="/dashboard" element={
                    <ProtectedRoute user={user} requiredModule="dashboard">
                      <Dashboard
                        clients={clients}
                        invoices={invoices}
                        onExport={handleExport}
                        onImport={handleImport}
                        config={config}
                        facilities={facilities}
                      />
                    </ProtectedRoute>
                  } />
                  <Route path="/invoice" element={
                    <ProtectedRoute user={user} requiredModule={(user?.moduleAccess?.invoice || user?.moduleAccess?.createInvoice || user?.moduleAccess?.adhocInvoice) ? 'invoice' : 'invoice'} allowedRoles={['admin', 'user']}>
                      <Invoice
                        clients={clients}
                        selectedClient={selectedClient}
                        setSelectedClient={setSelectedClient}
                        invoiceItems={invoiceItems}
                        setInvoiceItems={setInvoiceItems}
                        invoiceNumber={invoiceNumber}
                        setInvoiceNumber={setInvoiceNumber}
                        invoiceDate={invoiceDate}
                        setInvoiceDate={setInvoiceDate}
                        financialYear={financialYear}
                        setFinancialYear={setFinancialYear}
                        handleItemChange={handleItemChange}
                        addInvoiceItem={addInvoiceItem}
                        removeInvoiceItem={removeInvoiceItem}
                        generateInvoice={generateInvoice}
                        showInvoicePreview={showInvoicePreview}
                        setShowInvoicePreview={setShowInvoicePreview}
                        calculateTotals={calculateTotals}
                        numberToWords={numberToWords}
                        printRef={printRef}
                        handlePrint={handlePrint}
                        config={config}
                        poNumber={poNumber}
                        setPoNumber={setPoNumber}
                        poDate={poDate}
                        setPoDate={setPoDate}
                        purchaseInvoiceNumber={purchaseInvoiceNumber}
                        setPurchaseInvoiceNumber={setPurchaseInvoiceNumber}
                        purchaseInvoiceFile={purchaseInvoiceFile}
                        setPurchaseInvoiceFile={setPurchaseInvoiceFile}
                        isAdhocClientInvoice={isAdhocClientInvoice}
                        setIsAdhocClientInvoice={setIsAdhocClientInvoice}
                        vendorName={vendorName}
                        setVendorName={setVendorName}
                        vendorId={vendorId}
                        setVendorId={setVendorId}
                        vendorInvoiceDate={vendorInvoiceDate}
                        setVendorInvoiceDate={setVendorInvoiceDate}
                        vendorPoNumber={vendorPoNumber}
                        setVendorPoNumber={setVendorPoNumber}
                        vendorPoDate={vendorPoDate}
                        setVendorPoDate={setVendorPoDate}
                        department={department}
                        setDepartment={setDepartment}
                        showShipTo={showShipTo}
                        setShowShipTo={setShowShipTo}
                        facilities={facilities}
                        roundOffEnabled={roundOffEnabled}
                        setRoundOffEnabled={setRoundOffEnabled}
                        notes={notes}
                        setNotes={setNotes}
                      />
                    </ProtectedRoute>
                  } />
                  <Route path="/clients" element={
                    <ProtectedRoute user={user} requiredModule="clients">
                      <Clients
                        clients={clients}
                        onAddClient={addClient}
                        onUpdateClient={updateClient}
                        onRemoveClient={removeClient}
                        onExport={handleExport}
                        onImport={handleImport}
                        config={config}
                        facilities={facilities}
                        currentUser={user}
                      />
                    </ProtectedRoute>
                  } />
                  <Route path="/client/:clientId" element={
                    <ProtectedRoute user={user} requiredModule="clients">
                      <ClientDetails
                        clients={clients}
                        config={config}
                        facilities={facilities}
                        currentUser={user}
                        onUpdateClient={updateClient}
                        onRemoveClient={removeClient}
                      />
                    </ProtectedRoute>
                  } />
                  <Route path="/invoice/:id" element={
                    <ProtectedRoute user={user} requiredModule="invoiceHistory">
                      <InvoiceDetails currentUser={user} />
                    </ProtectedRoute>
                  } />
                  <Route path="/quotes/:id" element={
                    <ProtectedRoute user={user} requiredModule="quoteHistory">
                      <QuoteDetails currentUser={user} />
                    </ProtectedRoute>
                  } />
                  <Route path="/invoice-history" element={
                    <ProtectedRoute user={user} requiredModule="invoiceHistory">
                      <InvoiceHistory
                        printRef={printRef}
                        handlePrint={handlePrint}
                        currentUser={user}
                      />
                    </ProtectedRoute>
                  } />
                  <Route path="/expenses" element={
                    <ProtectedRoute user={user} requiredModule="expenses">
                      <Expenses
                        facilities={facilities}
                        currentUser={user}
                      />
                    </ProtectedRoute>
                  } />
                  <Route path="/profit-loss" element={
                    <ProtectedRoute user={user} requiredModule="profitLoss">
                      <ProfitLoss
                        invoices={invoices}
                        facilities={facilities}
                        clients={clients}
                      />
                    </ProtectedRoute>
                  } />
                  <Route path="/vendors" element={
                    <ProtectedRoute user={user} requiredModule="vendors">
                      <Vendors />
                    </ProtectedRoute>
                  } />
                  <Route path="/vendor/:id" element={
                    <ProtectedRoute user={user} requiredModule="vendors">
                      <VendorDetails currentUser={user} />
                    </ProtectedRoute>
                  } />
                  <Route path="/reports" element={
                    <ProtectedRoute user={user}>
                      <Reports facilities={facilities} clients={clients} />
                    </ProtectedRoute>
                  } />

                  <Route path="/create-quote" element={
                    <ProtectedRoute user={user} requiredModule="createQuote" allowedRoles={['admin', 'user']}>
                      <CreateQuote
                        clients={clients}
                        config={config}
                        user={user}
                        onQuoteNumberUpdate={(nextNumber) => {
                          setConfig((prev = {}) => ({ ...prev, nextQuoteNumber: nextNumber }));
                        }}
                      />
                    </ProtectedRoute>
                  } />
                  <Route path="/quote-history" element={
                    <ProtectedRoute user={user} requiredModule="quoteHistory">
                      <QuoteHistory clients={clients} config={config} users={users} currentUser={user} />
                    </ProtectedRoute>
                  } />
                  <Route path="/credit-notes" element={
                    <ProtectedRoute user={user} requiredModule="invoiceHistory">
                      <CreditNotes
                        invoices={invoices}
                        clients={clients}
                        config={config}
                        onInvoiceCreated={(note) => {
                          setInvoices((prev) => Array.isArray(prev) ? [...prev, note] : [note]);
                        }}
                      />
                    </ProtectedRoute>
                  } />
                  <Route path="/payables" element={
                    <ProtectedRoute user={user} requiredModule="payables">
                      <Payables facilities={facilities} currentUser={user} />
                    </ProtectedRoute>
                  } />
                  <Route path="/payable/:id" element={
                    <ProtectedRoute user={user} requiredModule="payables">
                      <PayableDetails currentUser={user} />
                    </ProtectedRoute>
                  } />
                  <Route path="/configurations" element={
                    <ProtectedRoute user={user} allowedRoles={['admin', 'user']} requiredModule="configurations">
                      <Configurations
                        facilities={facilities}
                        setFacilities={setFacilities}
                        currentUser={user}
                      />
                    </ProtectedRoute>
                  } />
                  <Route path="/users" element={
                    <ProtectedRoute user={user} requiredModule="users">
                      <UsersPage
                        currentUser={user}
                      />
                    </ProtectedRoute>
                  } />
                  <Route path="/term-sheets" element={
                    <ProtectedRoute user={user} requiredModule="termSheets">
                      <TermSheets
                        currentUser={user}
                      />
                    </ProtectedRoute>
                  } />
                  <Route path="/term-sheets/:id" element={
                    <ProtectedRoute user={user} requiredModule="termSheets">
                      <TermSheetDetails currentUser={user} />
                    </ProtectedRoute>
                  } />
                  <Route path="/profile" element={
                    <ProtectedRoute user={user}>
                      <Profile user={user} />
                    </ProtectedRoute>
                  } />
                </Routes>
              )}
              {isCountdownVisible && (
                <div className="session-countdown-overlay" role="alert" aria-live="assertive">
                  <div className="session-countdown-modal">
                    <h3>Session Timing Out</h3>
                    <p>Your session will end soon due to inactivity.</p>
                    <div className="session-countdown-timer">{sessionTimeLeft}s</div>
                    <button
                      type="button"
                      className="session-countdown-button"
                      onClick={handleStayLoggedIn}
                    >
                      Stay Signed In
                    </button>
                  </div>
                </div>
              )}
              {snackbarOpen && (
                <div style={{
                  position: 'fixed',
                  bottom: 32,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: '#43a047',
                  color: '#fff',
                  padding: '16px 32px',
                  borderRadius: 8,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  zIndex: 2000,
                  fontSize: 18,
                  fontWeight: 500
                }}>
                  {snackbarMessage}
                </div>
              )}
            </Layout.Content>
          </Layout>
        </Layout>
      </ConfigProvider>
    </Router>
  );
}

export default App; 